import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import async from 'async'

import { Icon } from '@src/elements/icon/icon'
import { bulletColorMapping, NotificationTypes } from '@src/util/constants'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { Legend } from '@src/components/legend/legend'
import { Divider } from '@src/elements/divider/divider'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Bullet } from '@src/elements/bullet/bullet'
import EventBus from '@src/lib/event_bus'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import Member from '@src/data/member'
import { convertToLocalDate } from '@src/util/date-format'
import SessionManager from '@src/lib/session_manager'

import './table.sass'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    }
  }
})
export class HistoryTable extends Vue {

  @Prop({ required: true })
  private history: any[]

  @Prop({ required: false, default: '' })
  private measuringUnit: string

  @Prop({ required: true })
  private title: string

  @Prop({ required: false })
  private translationKey: string

  // body composition history table rows can be selected
  @Prop({ required: false })
  private selectCallback?: (measurements: any[]) => any

  // data on the selected row (available only for body composition)
  @Prop({ required: false })
  private selectedRowData: any[]

  @Prop({ required: false })
  private legendItems: any[]

  @Prop({ required: false, default: false })
  private readonly: boolean

  @Prop({ required: false, default: function () { return [] } })
  private bodyWeights: any[]

  private selectedFilters: string[] = []

  private entityToDelete: any = null
  private showConfirmPopup: boolean = false

  private apiClient: ApiClient
  private member: Member

  constructor () {
    super()

    this.apiClient = new ApiClient()
  }

  showRemoveConfirm (id: any) {
    this.entityToDelete = id
    this.showConfirmPopup = true
  }

  confirmDelete (confirm: boolean) {
    if (confirm === true) {
      const bodyMeasurementIds: number[] = !isNaN(Number(this.entityToDelete)) ? [this.entityToDelete] : Object.keys(this.entityToDelete).map(key => this.entityToDelete[key])

      async.waterfall(
        bodyMeasurementIds.map((bodyMeasurementId) => {
          return done => this.delete(bodyMeasurementId, done)
        }),
        () => {
          EventBus.trigger('reloadBodyMeasurements')
          this.$store.dispatch('showToast', { message: this.$t('BODY_MEASUREMENTS.SAVE_SUCCESS'), fadeOut: true })
        })
    }
    this.showConfirmPopup = false
  }

  delete (bodyMeasurementId: number, done) {
    this.apiClient.delete(`${API.TRAINERS_API.BODY_MEASUREMENTS.replace('{memberId}', `${this.member.id}`)}/${bodyMeasurementId}`)
      .then(() => done(null))
      .catch(error => console.log('cannot delete body measurement', error))
  }

  filter (measurementType) {
    let index = this.selectedFilters.indexOf(measurementType)
    if (index >= 0) {
      this.selectedFilters.splice(index, 1)
    } else {
      this.selectedFilters.push(measurementType)
    }
  }

  selectRow (measurements: any[]) {
    if (this.selectCallback) {
      this.selectCallback(measurements)
    }
  }

  render (h: CreateElement) {
    const rows = this.history
      .filter((item) => { return this.legendItems ? this.selectedFilters.includes(item.type) : true })
      .sort((a, b) => {
        const firstElementDate = new Date(a.effectiveAt)
        const secondElementDate = new Date(b.effectiveAt)

        if (firstElementDate.getTime() < secondElementDate.getTime()) return 1
        if (firstElementDate.getTime() > secondElementDate.getTime()) return -1
        return 0
      })
      .map(item => {
        return <div class={`measurement-row ${this.selectCallback ? 'pointer' : ''} ${item.measurements && this.selectedRowData === item.measurements ? 'selected' : ''}`}
                  onClick={() => this.selectRow(item.measurements)}>
        <Bullet color={bulletColorMapping[item.type]} />
        <Subtitle1 class='value'>{item.value} {item.type !== 'body_vfat' ? this.measuringUnit : ''} {this.renderPercentage(item)}</Subtitle1>
        <Subtitle1>{convertToLocalDate(item.effectiveAt)}</Subtitle1>
        {!this.readonly && SessionManager.Instance.isManager() ? <Icon name='util/minus' click={() => this.showRemoveConfirm(item.id)} ref='deleteBtn' /> : null}
      </div>
      })

    return <div class='body-measurements-table'>
      <Subtitle1 class='table-header'>{this.title}</Subtitle1>

      <Divider />

      <div class='body-measurements-table-data-wrapper'>
        {this.renderLegend(h)}
        {this.renderRows(h,rows)}
      </div>
      {this.showConfirmPopup ?
        <ConfirmModal show={true}
                      closeCallback={this.confirmDelete}
                      message={this.$t('BODY_MEASUREMENTS.CONFIRM_REMOVE').toString()}
                      type={NotificationTypes.CONFIRM} /> : null}
    </div>
  }

  renderRows (h: CreateElement, rows) {
    if (!this.history.length) return <Subtitle1 class='no-results'>{this.$t('BODY_MEASUREMENTS.NO_DATA')}</Subtitle1>
    if (!rows.length) return <Subtitle1 class='no-results'>{this.$t('BODY_MEASUREMENTS.NO_RESULTS_FILTER')}</Subtitle1>
    return <div class='table-body-container'>{rows}</div>
  }

  renderPercentage (item) {
    if (this.bodyWeights.length !== 0) {
      if (['body_fat', 'muscle_mass', 'body_water_mass'].indexOf(item.type) >= 0) {
        let lastBodyWeight = null
        for (let i = 0; i < this.bodyWeights.length; i++) {
          if (this.bodyWeights[i].effectiveAt <= item.effectiveAt) lastBodyWeight = this.bodyWeights[i].value
        }
        if (lastBodyWeight) return `(${+((item.value * 100 / lastBodyWeight).toFixed(2))}%)`
      }
    }
  }

  renderLegend (h: CreateElement) {
    if (this.legendItems) {
      return <Legend select={this.filter} items={
        this.legendItems.map(item => {
          return { type: item, label: this.$t(`${this.translationKey}${item.toUpperCase()}`), color: bulletColorMapping[item], disabled: !this.selectedFilters.includes(item) }
        })
      } />
    }
  }

  created () {
    this.selectedFilters = Object.assign([], this.legendItems) || /* istanbul ignore next: Object.assign always returns the array */ []
  }
}
